/**
 * Les résultats d'une app comme entrée d'une autre (04/11/2026): an app's list
 * read as a table to import — its company, site and e-mail recognised by the
 * roles of its columns (L48, never their names), every other column a
 * parameter the person keeps or leaves out, only the objects of the chosen
 * statuses. The same table then goes through the import of a pasted table.
 */

import type { ImportColumn, ImportRole } from './table-import.ts';

export interface AppListColumn {
  key: string;
  label: string;
  role?: string | null;
}

export interface AppListObject {
  status: string;
  archived: boolean;
  cells: ReadonlyArray<{ key: string; value: unknown }>;
}

const ROLE_TO_IMPORT: Record<string, ImportRole> = { company: 'company', domain: 'domain', url: 'domain', email: 'email' };

const text = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : typeof value === 'number' || typeof value === 'boolean' ? String(value) : '';

/** What each column of the list becomes: its role, else the first column is the company, else a parameter. */
export const appColumnsForImport = (columns: ReadonlyArray<AppListColumn>): ImportColumn[] => {
  const companyByRole = columns.some((column) => column.role === 'company');
  const used = new Set<ImportRole>();
  return columns.map((column, index) => {
    let as: ImportRole = (column.role && ROLE_TO_IMPORT[column.role]) || (!companyByRole && index === 0 ? 'company' : 'parameter');
    if (as !== 'parameter' && used.has(as)) as = 'parameter';
    used.add(as);
    return { header: column.label || column.key, as };
  });
};

/** The list as a table: one row per object of the chosen statuses (all when none), archived ones left out. */
export const appListToTable = (params: {
  columns: ReadonlyArray<AppListColumn>;
  objects: ReadonlyArray<AppListObject>;
  statuses?: ReadonlyArray<string> | null;
}): { headers: string[]; rows: string[][]; mapping: ImportColumn[] } => {
  const keep = params.statuses && params.statuses.length > 0 ? new Set(params.statuses) : null;
  const rows = params.objects
    .filter((object) => !object.archived && (!keep || keep.has(object.status)))
    .map((object) => params.columns.map((column) => text(object.cells.find((cell) => cell.key === column.key)?.value)));
  return { headers: params.columns.map((column) => column.label || column.key), rows, mapping: appColumnsForImport(params.columns) };
};

/** How an app's output reads, for the chat to know what it can feed (one line per app). */
export const describeAppOutput = (params: {
  objectKind: string | null;
  columns: ReadonlyArray<AppListColumn>;
  statuses: ReadonlyArray<{ key: string; label: string }>;
}): string => {
  const kind = { company: 'entreprises', person: 'personnes', posting: 'offres', event: 'événements', other: 'objets' }[params.objectKind ?? 'other'] ?? 'objets';
  const columns = params.columns.map((column) => `${column.label}${column.role ? ` (${column.role})` : ''}`).join(', ');
  const statuses = params.statuses.map((status) => `${status.label} = ${status.key}`).join(', ');
  return `list of ${kind}: ${columns}${statuses ? `; statuses: ${statuses}` : ''}`;
};
